import React, { MouseEventHandler, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import cn from 'classnames';

import SearchInlineRecos from 'components/search/SearchInlineRecos';
import MicrosoftAdsWrapper from 'components/search/MicrosoftAdsWrapper';
import ProductCardQuickShopLink from 'components/common/ProductCardQuickShopLink';
import { evRecommendationClick } from 'events/recommendations';
import useMartyContext from 'hooks/useMartyContext';
import AdsRecosRow from 'components/search/AdsRecosRow';
import BrandContentSearchCarousel from 'components/influencer/brand/BrandContentSearchCarousel';
import { track } from 'apis/amethyst';
import { Product } from 'constants/searchTypes';
import ProductSearch from 'components/landing/ProductSearch';
import { useSponsoredAds } from 'hooks/useSponsoredAds';
import { PLACEMENT } from 'apis/sponsoredAds';
import { getProductRelations } from 'actions/products';

import css from 'styles/components/search/inlineAdSlot.scss';

interface PortalProps {
  WrapperEl?: string;
  titleClassName?: any;
  className: string;
  rowIndex?: number;
  attributes: { [key: string]: any };
  children?: Element;
}

interface Props {
  [key: string]: any;
}

export const InlineAdSlot = ({
  trackMicrosoftAdImpressions,
  makeCardClick,
  msftResults,
  heartsInfo,
  inlineRecos,
  filters: { sort, page, term },
  crossSiteRecos,
  isLoadingSymphony,
  sponsoredAds,
  getProductRelations,
  productCardsCount,
  forceNoMsftAds,
  brandContents,
  brandContentSlotData,
  rowIndex
}: Props) => {
  const {
    marketplace: {
      search: { msaMelodyImageParams, hasMicrosoftAds, hasSponsoredAds, microsoftAdsRowIndex, searchInlineRecosRowIndex }
    }
  } = useMartyContext();

  useEffect(() => {
    if (inlineRecos) {
      const styleIds = inlineRecos.recos.map((reco: { styleId: string }) => reco.styleId);
      getProductRelations(styleIds);
    }
  }, [inlineRecos]);

  const hasDefaultSort = !sort || sort?.bestForYou || sort?.relevance || !Object.keys(sort).length;
  const hasMarketplaceRecos = crossSiteRecos?.products?.length > 3 && !page && hasDefaultSort;
  const hasInlineRecos = inlineRecos?.recos?.length >= 2;
  const numMicrosoftAds = msftResults?.results?.length;
  const msftAdsToShow = !!numMicrosoftAds && hasMicrosoftAds;
  const numSponsoredAds = sponsoredAds?.results?.length;
  const sponsoredAdsToShow = !!numSponsoredAds && rowIndex && hasSponsoredAds;
  const hasBrandShopTheLook = brandContents?.length && brandContentSlotData?.rowPositions?.length;

  const makeMicrosoftAdClick = (product: Product, index: number) => {
    const onCardClick = makeCardClick(product, index);
    const { productId, styleId, colorId } = product;
    return (e: MouseEventHandler) => {
      track(() => [
        evRecommendationClick,
        {
          index,
          recommendationType: 'PRODUCT_RECOMMENDATION',
          productIdentifiers: {
            productId,
            styleId,
            colorId
          },
          recommendationSource: 'MICROSOFT',
          widgetType: 'MICROSOFT_TOP_BLOCK',
          sourcePage: 'SEARCH_PAGE'
        }
      ]);

      onCardClick(e);
    };
  };

  // default is P13N product ads
  let portalProps: PortalProps = { className: '', attributes: {} };
  let portalContent;

  if (isLoadingSymphony) {
    return null;
  }

  if (hasInlineRecos) {
    portalProps = {
      WrapperEl: 'aside',
      className: css.inlineAdWrapper,
      rowIndex: searchInlineRecosRowIndex,
      attributes: {
        'data-reco-count': inlineRecos.recos.length,
        'data-test-id': 'searchResultsRecos'
      }
    };

    portalContent = (
      <SearchInlineRecos
        titleClassName={css.inlineTitle}
        heartsInfo={heartsInfo}
        inlineRecos={inlineRecos}
        msaImageParams={msaMelodyImageParams}
        searchTerm={term}
        CardBottomSlot={ProductCardQuickShopLink}
      />
    );
  }

  // new implementation of sponsored ads endpoint.
  if (sponsoredAds) {
    // we need a better way to handle this.
    // const hasSecondRowAds = true || hasInlineRecos || msftAdsToShow || hasMarketplaceRecos;

    //if second row ads are showing, we need to remove one from the index to get correct the desired row.
    portalProps = {
      className: cn([css.microsoftAdsWrapper, `sponsoredAds-${rowIndex}`]),
      rowIndex,
      attributes: { 'data-test-id': `sponsoredResultsAds-${rowIndex}` }
    };

    portalContent = (
      <MicrosoftAdsWrapper
        numMicrosoftAds={numSponsoredAds}
        trackMicrosoftAdImpressions={trackMicrosoftAdImpressions} //tracks viewUrl per product.
        msftResults={sponsoredAds}
        msaImageParams={msaMelodyImageParams}
        makeMicrosoftAdClick={makeMicrosoftAdClick}
        msftAdsToShow={sponsoredAdsToShow}
        CardBottomSlot={ProductCardQuickShopLink}
      />
    );
  }

  // Microsoft Ads - original implementation, to be deprecated in favor of sponsoredAdsSlotOneRowIndex
  // force no msftAds to show if we want to use the other one because they both can exist.
  if (msftAdsToShow && !forceNoMsftAds) {
    portalProps = {
      className: css.microsoftAdsWrapper,
      rowIndex: microsoftAdsRowIndex,
      attributes: { 'data-test-id': 'searchResultsAds' }
    };

    portalContent = (
      <MicrosoftAdsWrapper
        numMicrosoftAds={numMicrosoftAds}
        trackMicrosoftAdImpressions={trackMicrosoftAdImpressions}
        msftResults={msftResults}
        msaImageParams={msaMelodyImageParams}
        makeMicrosoftAdClick={makeMicrosoftAdClick}
        msftAdsToShow={msftAdsToShow}
        CardBottomSlot={ProductCardQuickShopLink}
      />
    );
  }

  // Marketplace Recos
  if (hasMarketplaceRecos) {
    portalProps = {
      className: css.marketplaceRecosWrapper,
      rowIndex: searchInlineRecosRowIndex,
      attributes: { 'data-test-id': 'marketplaceRecos' }
    };

    const slotHeartsData = { onHeartClick: '', hearts: '', heartsList: '' };

    portalContent = (
      <ProductSearch
        slotIndex={0}
        slotName={'marketplaceRecos'}
        slotHeartsData={slotHeartsData}
        onComponentClick={() => {}}
        shouldLazyLoad={false}
        isFullWidth={false}
        slotDetails={crossSiteRecos}
      />
    );
  }

  // Brand Influencer Shop The Look
  if (hasBrandShopTheLook) {
    portalProps = {
      className: css.brandShopTheLookWrapper,
      rowIndex: brandContentSlotData.rowPositions[0] - 1,
      attributes: { 'data-test-id': 'brandShopTheLookRecos' }
    };
    portalContent = (
      <BrandContentSearchCarousel searchTerm={term} brandContents={brandContents} rowPosition={brandContentSlotData.rowPositions[0]} />
    );
  }

  return (
    <AdsRecosRow {...portalProps} cardCount={productCardsCount} debounceWait={100}>
      {portalContent}
    </AdsRecosRow>
  );
};

interface InlineSponsoredAdsProps extends Props {
  placement: PLACEMENT;
  term?: string;
}

export const InlineSponsoredAds = (props: InlineSponsoredAdsProps) => {
  const selected = useSelector(state => state.filters.selected);

  const filters = { ...selected?.singleSelects, ...selected?.multiSelects };

  const pageType = Object.keys(filters).length > 0 ? 'Category' : 'SearchResults';

  const sponsoredAds = useSponsoredAds({ pageType, filters, placementId: props.placement, term: props.term, minRes: 4, maxRes: 4 });

  return <ConnectedInlineAdSlot {...props} sponsoredAds={sponsoredAds} forceNoMsftAds={true} />;
};

export const InlineBrandShopTheLook = (props: Props) => <ConnectedInlineAdSlot {...props} />;

const ConnectedInlineAdSlot = connect(null, { getProductRelations })(InlineAdSlot);
export default ConnectedInlineAdSlot;
